<template>
  <li>
    <div class="row">
      <img
        v-if="$userIsClient"
        :src="talent.profile_image_url"
        :class="{
          'col-xs-6' : $userIsClient,
          'col-xs-0' : $userIsAdmin || $userIsManager
        }">
      <div class="col-md-12 col-lg-12 response-col"
           :class="{
          'col-xs-6' : $userIsClient,
          'col-xs-12' : $userIsAdmin || $userIsManager
        }">
        <div
          v-bind:class="{
            'col-lg-9 col-md-7 col-sm-12 col-xs-12': $userIsAdmin || $userIsManager,
            'col-lg-9 col-md-9 col-sm-12 col-xs-12': ($userIsClient && (talent.response.expiry_date === null))
          }">

          <h3>{{ talent.name }}</h3>

        </div>
        <div
          v-if="$userIsAdmin || $userIsManager"
          class="col-xs-12 col-sm-6 col-md-4 col-lg-3 spacer-y-1"
          :class="{ 'col-lg-offset-3 col-md-offset-4': $userIsManager }">

          <ladda-button
            class="btn btn-red btn-block"
            :click="handleRemoveTalentCallback">
            <i class="fa fa-trash"></i> Remove
          </ladda-button>
        </div>
      </div>

    </div>

    <div style="height: 2px;"></div>

    <booking-talent-response-decline-modal
      ref="modal"
      v-model="declineRequestReason"
      @comment-added="(comment) => { this.comment = comment; this.decline_modal_submitted = true }"
      :booking="booking"/>

    <booking-talent-date-applied-for-modal
      v-if="booking.has_date_options_only"
      v-model="dateAppliedFor"
      :talentId="talent.id"
      :booking="booking"
      :dates="booking.date_options" />
  </li>
</template>

<script>
import BookingTalentResponseDeclineModal from './BookingTalentResponseDeclineModal'
import BookingTalentDateAppliedForModal from './BookingTalentDateAppliedForModal'
import moment from 'moment-timezone'
import $ from 'jquery'
import { mapActions, mapState } from 'vuex'
import { alert, notify, publicUrl } from 'utils'
import { helpers } from 'mixins/bookings'
import Selectize from '../ext/codium/CodiumSelectize'
import CountdownAdmin from '../misc/CountdownAdmin'
import CountdownClient from '../misc/CountdownClient'
import { OpportunityTracker } from 'mixins/analytics'

export default {
  mixins: [helpers, OpportunityTracker],

  props: {
    talent: {
      required: true,
      type: Object
    },
    booking: {
      required: true,
      type: Object
    }
  },

  components: {
    Selectize,
    CountdownAdmin,
    CountdownClient,
    BookingTalentResponseDeclineModal,
    BookingTalentDateAppliedForModal
  },

  data () {
    return {
      talentWasUpdated: false,
      declineRequestPromise: null,
      declineRequestReason: null,
      previousResponseStatus: null,
      dateAppliedForPromise: null,
      dateAppliedFor: null,
      decline_modal_submitted: false,
      showCommentInputField: false,
      hovering: false,
      comment: (this.talent
        ? ((this.talent.response.comment !== null)
          ? this.talent.response.comment
          : '')
        : '')
    }
  },

  /**
   * On created we locally track the talents response status. This is so we can override this
   * response status if we are an administrator.
   *
   * @return void
   */
  created () {
    if (this.$route.params.contact !== undefined) {
      if (this.$route.params.contact === 'contact') {
        const infoAlertOptions = {
          icon: 'info',
          dangerMode: false,
          title: 'Contact Us to Reactivate ' + this.$options.filters.sentenceCase(this.$starLabel),
          text: 'If you missed out on booking a talent and would like to reactivate their application, contact ' + this.clientName + ' using any of the methods below and we will see if the talent is still available for the event.\n Phone: ' + this.contactNumber + ' \n Email: ' + this.contactEmail
        }
        return alert.info(infoAlertOptions)
      }
    }
  },

  watch: {
    stateTalentResponseStatus (talentResponseStatus, prevTalentResponseStatus) {
      if ((this.$userIsAdmin || this.$userIsManager)) {
        this.handleTalentResponseStatusChangedCallback(talentResponseStatus, prevTalentResponseStatus)
      }
    }
  },

  computed: {
    ...mapState('clientConfig', ['client_config']),
    /*
     * Whether to show the date_applied_for in 'Book Talent' and 'Talent Applied'
     *
     * @return boolean
     */
    showDateAppliedFor () {
      return this.talent.response.date_applied_for && this.booking.date_options && this.booking.date_options.length
    },

    responseUpdatedAt () {
      return moment.utc(this.talent.response.comment_updated_at).fromNow()
    },

    responseUpdatedBy () {
      if (this.talent && this.talent.response && this.talent.response.comment_updated_by) {
        const commenterRole = this.talent.response.comment_updated_by.role
        return (commenterRole.toLowerCase() === 'talent' || commenterRole.toLowerCase() === 'client') ? commenterRole : this.clientName
      }
    },

    canAddCommentForTalent () {
      if (!(this.booking.talent_can_comment || this.$commentModule)) {
        return false
      }
      if (!(this.$userIsAdmin || this.$userIsManager)) {
        return false
      }
      return (this.talent.response.status === 'applied' || (this.$declineCommentModuleEnabled && this.talent.response.status === 'declined'))
    },

    commentPlaceholderText () {
      if (this.talent.response.status === 'applied') {
        return 'Please give us some more information on why ' + this.talent.first_name + ' would be a great fit! \nNote: this comment will be visible to the client and admin and any discussions regarding ' + this.$clientConfig.budget_label + ' need to be had with ' + this.clientName + ', not through this comment box.'
      }
      if (this.talent.response.status === 'declined') {
        return 'Please give us some more information on why ' + this.talent.first_name + ' is declining.'
      }
    },

    /*
     * Whether an applied talent can be booked.
     *
     * @return boolean
     */
    canBeBooked () {
      if (this.bookingAppearanceStrictlyOnline) {
        return true
      }
      if (this.booking.has_date_options_only) {
        return this.booking.date_options.includes(this.talent.response.date_applied_for)
      } else {
        if (this.$vlastMode && this.$allowBookingDateRange) {
          return true
        }
        return this.booking.date === this.talent.response.date_applied_for
      }
    },

    stateTalentResponseStatus: {
      get () {
        return this.talent.response.status
      },

      set (value) {
        this.setRequestTalentProperty({ id: this.talent.id, property: 'response.status', value })
      }
    },

    talentResponseReviewing () {
      return this.talent.response.status === 'reviewing'
    },

    talentResponseApplied () {
      return this.talent.response.status === 'applied'
    },

    talentResponseDeclined () {
      return this.talent.response.status === 'declined'
    },

    talentResponseExpired () {
      return this.talent.response.expired
    },

    talentResponseDeclineReason () {
      if (this.talent.response.decline_reason === 'information') {
        return 'Need more information'
      } else if (this.talent.response.decline_reason === 'interest') {
        return 'Not interested'
      } else if (this.talent.response.decline_reason === 'scheduling') {
        return 'Scheduling issues'
      } else if (this.talent.response.decline_reason === 'budget') {
        return 'Budget too low'
      } else if (this.talent.response.decline_reason === 'manager') {
        return 'Manager Declined'
      } else if (this.talent.response.decline_reason === 'pickstar') {
        return 'Pickstar Declined'
      } else {
        return 'No Reason Given'
      }
    },

    talentResponseAccepted () {
      return this.talent.response.client_accepted
    },

    showComment () {
      if (!this.talent.response.comment) {
        return false
      }
      if (this.$userIsAdmin || this.$userIsManager) {
        return true
      }
      if (this.talent.response.status === 'declined') {
        if (!this.$vlastMode) {
          return this.booking.talent_can_comment &&
            this.$declineCommentModuleEnabled &&
            this.talent &&
            this.talent.response &&
            this.talent.response.shortlisted
        }
        return this.booking.talent_can_comment || this.$declineCommentModuleEnabled
      }
      if (this.talent.response.status === 'applied') {
        return this.booking.talent_can_comment || this.$commentModule
      }
      return false
    },

    showCustomBudget () {
      return (this.talent.response.budget && this.talent.response.budget !== this.booking.budget_per_star) || (this.talent.response.budget_per_star_as_user_rate && this.talent.response.budget_per_star_as_user_rate !== this.booking.budget_per_star_as_user_rate)
    },

    userCannotUpdateTalentResponse () {
      if (this.bookingIsStandardPackage && this.bookingIsNotPending) {
        return true
      }
      if (this.$userIsManager) {
        return this.bookingTypeIsBooking ||
          this.bookingIsNotPending
      }
      if (this.$userIsNonGlobalAdmin) {
        return this.booking.region_id !== this.$user.region_id && this.talent.region_id !== this.$user.region_id
      }
      return false
    },

    userCanAcceptMoreTalent () {
      return (this.$userIsAdmin || this.$userIsClient) &&
        this.bookingAttendingTalent.length < this.booking.required_stars
    },

    profileUrl () {
      return this.$vlastMode ? '#' : publicUrl(`our-stars/${this.talent.id}/${this.talent.slug}`)
    },

    /**
     * Array of options for the response status.
     *
     * @return array
     */
    talentResponseStatusOptions () {
      // Hide all available options from clients.
      if (this.$userIsClient) {
        return []
      }

      let options = [
        { id: 'applied', text: this.$options.filters.sentenceCase(this.$starLabel) + ' ' + this.$t('Selected')}
      ]

      if (this.$userIsAdmin) {
        options.push({ id: 'remove', text: 'Remove' })
      }

      return options
    }
  },

  mounted () {
    $(this.$refs.modal.$el).on('hidden.bs.modal', this.handleDeclineRequestModalHiddenCallback)
    $('#modal-dates-' + this.talent.id).on('hidden.bs.modal', this.handleDatesModalHiddenCallback)

    // $('#modal-dates-' + this.talent.id).on('hidden.bs.modal', this.handleOnDateModalClose)
  },

  methods: {
    ...mapActions('booking/request', [
      'acceptBookingRequestAsNonTalent',
      'acceptBookingRequestTalent',
      'unselectBookingRequestTalent',
      'updateBookingRequestTalent',
      'cancelBookingRequestForTalent',
      'deleteBookingRequestTalent',
      'deleteBookingOfferRequestTalent',
      'reactivateTalentApplication',
      'changeApplicationComment',
      'replaceRequestTalent',
      'removeBookingRequestShortlistedTalent',
      'setRequestTalentProperty',
    ]),

    toggleShowCommentInputField () {
      this.showCommentInputField = !this.showCommentInputField
    },

    /**
     * Show date_options modal
     */
    showDatesModal () {
      $('#modal-dates-' + this.talent.id).modal('show')
    },

    /**
     * If multiple are available, confirm with the client that by booking the selected talent
     * the event date of the booking will be set to the date that the talent applied for.
     *
     * @return Promise
     */
    handleConfirmEventDate () {
      if (this.booking.has_date_options_only) {
        let selectedDate = this.$options.filters.date(this.talent.response.date_applied_for)
        const confirmAlertOptions = {
          title: 'Confirm the event date',
          text: `This ${this.$starLabel} has selected ` + selectedDate + ' as their preferred date option.',
          buttons: {
            confirm: {
              text: 'Confirm'
            }
          }
        }

        return alert.confirm(confirmAlertOptions)
          .then(() => {
            return this.handleAcceptTalentCallback()
          })
      } else {
        return this.handleAcceptTalentCallback()
      }
    },

    /**
     * Handle updating the talent's new status.
     *
     * @return Promise
     */
    handleUpdateRequestCallback (comment = null, dateAppliedFor = null) {
      const payload = {
        id: this.booking.id,
        talentId: this.talent.id,
        payload: {
          status: this.stateTalentResponseStatus,
          previousStatus: this.previousResponseStatus,
          dateAppliedFor: dateAppliedFor,
          comment: comment,
          decline_reason: this.talent.response.decline_reason
        }
      }

      if (comment) {
        if (this.talent.response.comment) {
          this.trackEvent(this.analytics.V2.JobCommentEditEvent)
        } else {
          this.trackEvent(this.analytics.V2.JobCommentAddEvent)
        }
      }

      if (this.stateTalentResponseStatus === 'accepted') {
        this.trackEvent(this.analytics.V2.JobPackageAcceptEvent)
        return this.acceptBookingRequestAsNonTalent(payload).then(() => {
          this.$router.push({name: 'bookings.show', params: { id: this.booking.id }})
        })
      } else {
        if ((this.previousResponseStatus !== 'applied' && this.previousResponseStatus !== null) && this.stateTalentResponseStatus === 'applied') {
          this.trackEvent(this.analytics.V2.JobApplyEvent)
        }

        if ((this.previousResponseStatus !== 'declined' && this.previousResponseStatus !== null) && this.stateTalentResponseStatus === 'declined') {
          this.trackEvent(this.analytics.V2.JobDeclineEvent)
        }
        return this.updateBookingRequestTalent(payload)
          .then((response) => {
            this.replaceRequestTalent({ id: this.talent.id, talent: response.data })
            this.showCommentInputField = false
            this.comment = response.data.response.comment
            return response
          })
          .then(() => {
            notify.success(this.$options.filters.sentenceCase(this.$starLabel) + ' response has been updated.')
            return Promise.resolve()
          })
      }
    },

    /**
     * Handle accepting a talent with alert
     *
     * @return Promise
     */
    handleAcceptTalentCallbackWithAlert () {
      const confirmAlertOptions = {
        icon: 'warning',
        dangerMode: false,
        title: 'Book ' + this.$options.filters.sentenceCase(this.$starLabel),
        text: `Are you sure you want to book ${this.talent.name}?`,
        buttons: {
          confirm: {
            text: 'Yes, book ' + this.$starLabel
          }
        }
      }

      return alert.confirm(confirmAlertOptions)
        .then(() => {
          return this.acceptBookingRequestTalent({
            id: this.booking.id,
            talentId: this.talent.id
          })
        })
        .then((response) => {
          this.talentWasUpdated = true

          this.replaceRequestTalent({ id: this.talent.id, talent: response.data })
        })
    },

    handleUnselectTalentCallback () {
      const confirmAlertOptions = {
        icon: 'warning',
        dangerMode: false,
        title: 'Unselect ' + this.$options.filters.sentenceCase(this.$starLabel),
        text: `Are you sure you want to unselect ${this.talent.name}?`,
        buttons: {
          confirm: {
            text: 'Yes, unselect ' + this.$starLabel + '.'
          }
        }
      }
      return alert.confirm(confirmAlertOptions).then(() => {
        return this.removeBookingRequestShortlistedTalent({
          id: this.booking.id,
          talentId: this.talent.id
        }).then((response) => {
          this.talentWasUpdated = true
          this.replaceRequestTalent({ id: this.talent.id, talent: response.data })
          this.trackEvent(this.analytics.V2.JobStarUnselectEvent)
        }).catch((error) => {
          notify.error(error.response.data.message)
        })
      })
    },

    handleRemoveTalentCallback() {
      const payload = {
        id: this.booking.id,
        talentId: this.talent.id
      }

      return this.deleteBookingOfferRequestTalent(payload)
        .then(() => {
          window.location.replace(`/bookings/${this.booking.id}/overview`)
        })
    },

    /**
     * Handle accepting a talent.without alert
     *
     * @return Promise
     */
    handleAcceptTalentCallback () {
      return this.acceptBookingRequestTalent({
        id: this.booking.id,
        talentId: this.talent.id
      }).then((response) => {
        this.talentWasUpdated = true
        // Get a fresh booking resource, since the event date might have been set from date options
        // If so, all booking-response-list-items will be refreshed and those who applied for a different
        // date will become 'unbookable'
        // this.getBookingOffer({id: this.booking.id})
        this.replaceRequestTalent({ id: this.talent.id, talent: response.data })
        this.trackEvent(this.analytics.V2.JobStarSelectEvent)
      }).catch((error) => {
        notify.error(error.response.data.message)
      })
    },

    /**
     * Handle reactivating a talent with alert
     *
     * @return Promise
     */
    handleReactivateTalentApplicationCallback () {
      const confirmAlertOptions = {
        icon: 'warning',
        dangerMode: false,
        title: 'Reactivate ' + this.$options.filters.sentenceCase(this.$starLabel),
        text: `Are you sure you want to reactivate ${this.talent.name}'s application? The client will then be able to book them.`,
        buttons: {
          confirm: {
            text: 'Yes, reactivate ' + this.$starLabel + ' application.'
          }
        }
      }

      return alert.confirm(confirmAlertOptions)
        .then(() => {
          return this.reactivateTalentApplication({
            id: this.booking.id,
            payload: {
              talent_id: this.talent.id
            }
          })
        })
        .then((response) => {
          this.talentWasUpdated = true

          this.replaceRequestTalent({ id: this.talent.id, talent: response.data })
        })
    },

    /**
     * Handle client pressing contact us button
     */
    handleContactUsCallback () {
      const infoAlertOptions = {
        icon: 'info',
        dangerMode: false,
        title: 'Contact Us to Reactivate ' + this.$options.filters.sentenceCase(this.$starLabel),
        text: `If you missed out on booking ${this.talent.name} and would like to reactivate their application, contact ${this.clientName} using any of the methods below and we will see if the talent is still available for the event.\n Phone: ${this.contactNumber} \n Email: ${this.contactEmail}`
      }
      return alert.info(infoAlertOptions)
    },



    /**
     * Handle talent response status being changed by admin or manager.
     *
     * @return Promise
     */
    handleTalentResponseStatusChangedCallback (talentResponseStatus, previousResponseStatus) {
      console.warn('----> handleTalentResponseStatusChangedCallback', '#talentResponseStatus', talentResponseStatus, '#previousResponseStatus', previousResponseStatus)

      // If user cancels any dateSelect or decline modal, then we can revert the status
      this.previousResponseStatus = previousResponseStatus

      // If talent was just updated then we want to prevent the response status watch
      // from being triggered in this instance.'
      if (this.talentWasUpdated) {
        this.talentWasUpdated = false
        return
      }

      if (this.stateTalentResponseStatus === 'remove') {
        // If the talent response status selected is "remove" then we'll send a delete
        // request which detaches the talent from the booking request.
        const payload = {
          id: this.booking.id,
          talentId: this.talent.id
        }

        return this.deleteBookingRequestTalent(payload)
          .then(() => {
            this.removeBookingRequestShortlistedTalent(this.talent.id)
          })
      }

      if (this.$userIsManager && this.stateTalentResponseStatus === 'declined') {
        this.handleDeclineRequestCallback()
      } else {
        // Show the dates selection modal if date_options is available and date is not.
        // Return a promise that will handle the selected date.
        if (this.stateTalentResponseStatus === 'applied' || this.stateTalentResponseStatus === 'accepted') {
          if (this.booking.has_date_options_only) {
            this.dateAppliedFor = null
            this.showDatesModal()

            const defineDateAppliedForPromise = (resolve, reject) => {
              this.dateAppliedForPromise = { resolve, reject }
            }

            return new Promise(defineDateAppliedForPromise)
              .then(() => {
                this.handleUpdateRequestCallback(this.comment, this.dateAppliedFor)
              })
          }
        }

        // If the response status has been changed to anything but "remove" then we'll
        // send an update talent response request with the new status to set. A
        // previously accepted talent will no longer be accepted by the client.

        return this.handleUpdateRequestCallback()
      }
    },

    /**
     * Handle declining with a reason
     */
    handleDeclineRequestCallback () {
      this.declineRequestReason = null
      $(this.$refs.modal.$el).modal('show')

      const defineDeclineRequestPromise = (resolve, reject) => {
        this.declineRequestPromise = {resolve, reject}
      }

      return new Promise(defineDeclineRequestPromise)
        .then(() => {
          const payload = {
            id: this.booking.id,
            talentId: this.talent.id,
            payload: {
              status: this.stateTalentResponseStatus,
              decline_reason: this.declineRequestReason,
              comment: this.comment
            }
          }
          if (this.bookingIsStandardPackage) {
            return this.cancelBookingRequestForTalent({ id: this.booking.id, reason: this.declineRequestReason, comment: this.comment, talentId: this.talent.id })
              .then(() => {
                this.$router.go()
              })
          } else {
            this.trackEvent(this.analytics.V2.JobDeclineEvent)
            return this.updateBookingRequestTalent(payload)
              .then((response) => {
                this.replaceRequestTalent({ id: this.talent.id, talent: response.data })
              })
              .then(() => {
                notify.success(this.$options.filters.sentenceCase(this.$starLabel) + ' response has been updated.')
              })
          }
        })
        .catch(() => {
          this.talentWasUpdated = true
          this.stateTalentResponseStatus = this.previousResponseStatus
        })
    },

    /**
     * Handle the decline request modal being .
     *
     * @return void
     */
    handleDeclineRequestModalHiddenCallback () {
      // If our modal component emitted a decline reason then we'll resolve the promise as we'll
      // continue with declining the request.
      if (this.declineRequestReason || (this.$declineCommentModuleEnabled && this.decline_modal_submitted)) {
        this.declineRequestPromise.resolve()
        this.decline_modal_submitted = false

        // If no reason was emitted then the modal was closed so we'll reject the promise to
        // prevent the declining.
      } else {
        this.declineRequestPromise.reject()
      }

      this.declineRequestPromise = null
    },

    /**
     * Handle admin deleting a talents their comment.
     *
     * @return Promise
     */
    handleDeleteCommentCallback () {
      return this.changeApplicationComment({ id: this.booking.id, comment: null, talentId: this.talent.id })
        .then((response) => {
          this.replaceRequestTalent({ id: this.talent.id, talent: response.data })
          this.comment = null
          notify.success('Successfully deleted talent\'s comment.')
        })
    },

    /**
     * Handle the dates modal being hidden.
     *
     * @return void
     */
    handleDatesModalHiddenCallback () {
      // If our modal component emitted a dateAppliedFor then we'll resolve the promise as we'll
      // continue with applying for the booking
      if (this.dateAppliedFor) {
        this.dateAppliedForPromise.resolve()

        // If no dateAppliedFor emitted then the modal was closed so we'll reject the promise to
        // prevent the applying. Also set the selectize value to the previousResponseStatus.
      } else {
        this.dateAppliedForPromise.reject()
        this.talentWasUpdated = true
        this.stateTalentResponseStatus = this.previousResponseStatus
      }

      this.dateAppliedForPromise = null
    },

    handleAddCommentCallback () {
      return this.handleUpdateRequestCallback(this.comment)
    }
  }
}
</script>

<style lang="scss">
  .response-text-area{
    resize: none;
  }
  .response-list-item-delete-button{
    margin-top: 15px !important;
  }
  .commenter{
    color: var(--brand-default-dark);
    font-size: 12px;
  }
  .btn-skip-comment{
    padding: 10px 25px;
    margin-right: 8px;
  }

  .my-cool-button {
    &:hover {
      background-color: var(--brand-success-dark-transparent);
      border-color: var(--brand-success-dark);
     }
  }
</style>
